import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck, Handshake } from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/motion";
import { Container, SectionHeading } from "@/components/Section";
import { IMAGES } from "@/data/content";

export default function About() {
  return (
    <main data-testid="about-page">
      <section className="noise-dark bg-forest pb-24 pt-44 text-cream sm:pb-32 sm:pt-52">
        <Container className="relative z-10">
          <FadeUp>
            <p className="eyebrow text-sage">About FieldGreen</p>
          </FadeUp>
          <MaskedLines
            className="mt-6 max-w-4xl"
            lineClassName="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.06]"
            lines={[
              <span key="1">Property management built</span>,
              <span key="2" className="italic text-sage-light">from an owner's perspective.</span>,
            ]}
          />
        </Container>
      </section>

      <section className="bg-cream py-24 sm:py-32">
        <Container className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <p className="eyebrow text-forest-700">Our story</p>
              <h2 className="mt-5 font-serif text-4xl font-medium leading-tight text-forest">
                Dublin roots. Commercial instincts. Hospitality standards.
              </h2>
            </FadeUp>
          </div>
          <div className="space-y-6 text-base leading-relaxed text-charcoal/70 lg:col-span-7">
            <FadeUp delay={0.1}>
              <p>
                FieldGreen was created around a simple idea: property owners should be able to benefit from the
                short-term rental market without taking on another full-time job.
              </p>
            </FadeUp>
            <FadeUp delay={0.18}>
              <p>
                Founder Andrew spent close to a decade in Dublin's technology sales environment — a world
                where performance is measured, processes are systemised and customers expect a professional
                experience. He's also a Dublin property owner and landlord himself, so he knows exactly what it
                feels like to hand the keys to someone else.
              </p>
            </FadeUp>
            <FadeUp delay={0.26}>
              <p>
                FieldGreen sits at the intersection of those two worlds: the commercial discipline of technology
                sales and the operational care of hospitality. We run each property like a small business —
                priced on data, presented properly, and managed by people who answer the phone.
              </p>
            </FadeUp>
          </div>
        </Container>
      </section>

      <section className="bg-bone py-24 sm:py-32">
        <Container className="grid items-center gap-16 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5">
            <div className="relative">
              <div className="overflow-hidden rounded-2xl">
                <img
                  src={IMAGES.founder}
                  alt="Andrew, founder of FieldGreen Property Management"
                  className="aspect-[4/5] w-full object-cover"
                  data-testid="about-founder-photo"
                />
              </div>
              <div className="absolute -bottom-6 -right-4 rounded-xl bg-forest px-6 py-5 text-cream sm:-right-8">
                <p className="font-serif text-xl font-medium">Andrew</p>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-sage">Founder</p>
              </div>
            </div>
          </FadeUp>
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="The founder" title="A commercially minded operator who owns property too." />
            <FadeUp delay={0.2}>
              <ul className="mt-9 space-y-5">
                {[
                  "Around 10 years in Dublin's technology sales environment",
                  "Dublin property owner and landlord first-hand",
                  "Commercially focused approach to pricing and revenue",
                  "Obsessive about combining hospitality, technology and revenue optimisation",
                ].map((t, i) => (
                  <li key={t} className="flex items-start gap-4 border-b border-forest/10 pb-5" data-testid={`founder-fact-${i}`}>
                    <span className="font-mono text-xs text-forest-700/60 pt-1">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-base font-medium text-charcoal/80">{t}</p>
                  </li>
                ))}
              </ul>
            </FadeUp>
            <FadeUp delay={0.3}>
              <div className="mt-9 rounded-2xl bg-forest p-8 text-cream">
                <p className="font-serif text-xl italic leading-relaxed" data-testid="about-founder-quote">
                  "I wanted the kind of management company I'd hand my own property to — one that treats revenue
                  like a discipline and guests like customers. That's FieldGreen."
                </p>
                <p className="mt-4 text-xs uppercase tracking-[0.2em] text-sage">Andrew</p>
              </div>
            </FadeUp>
          </div>
        </Container>
      </section>

      <section className="bg-cream py-24 sm:py-32">
        <Container>
          <SectionHeading eyebrow="How we operate" title="Three standards we don't compromise on." />
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {[
              {
                icon: MapPin,
                t: "Local, on purpose",
                d: "We manage in Dublin because we know Dublin — its neighbourhoods, demand cycles and guests. Local knowledge is a performance advantage.",
              },
              {
                icon: ShieldCheck,
                t: "Straight answers",
                d: "If your property isn't right for short-term letting, we'll tell you — and tell you why. No inflated projections, no pressure.",
              },
              {
                icon: Handshake,
                t: "One accountable team",
                d: "You never coordinate cleaners, contractors and guest messages yourself. One point of contact, one monthly statement.",
              },
            ].map(({ icon: Icon, t, d }, i) => (
              <FadeUp key={t} delay={i * 0.08} className="h-full">
                <div
                  data-testid={`value-card-${i}`}
                  className="flex h-full flex-col rounded-2xl border border-forest/12 bg-bone p-9 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(15,38,28,0.12)]"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-forest text-sage">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl font-medium text-forest">{t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/65">{d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </Container>
      </section>

      <section className="noise-dark bg-forest py-24 text-cream sm:py-28">
        <Container className="relative z-10 flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-center">
          <div className="max-w-xl">
            <FadeUp>
              <h2 className="font-serif text-4xl sm:text-5xl font-medium">Let's talk about your property.</h2>
              <p className="mt-5 text-base leading-relaxed text-cream/65">
                A realistic assessment, an honest conversation, and a clear plan if it makes sense.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.15}>
            <Link
              to="/assessment"
              data-testid="about-bottom-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-cream px-7 py-4 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage"
            >
              Get a Property Assessment
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </Container>
      </section>
    </main>
  );
}
